import React, { useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { LikeButton } from '@components/Likes/Button';
import { useLikeRelease } from '@components/Likes/useLikeRelease';

import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ViewSongMenuItem } from '@components/basics/Dropdowns/MenuItems/ViewSongMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import {
  PlayContentWrapper,
  PlayIndicatorBars,
} from '@components/main-components/cards/PlayIndicatorBars';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { ReleaseRowListFragmentDoc } from '@graphql/reactQuery';
import { useCurrentListeningParty } from '@hooks/usePlayButton';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseRowList on Release {
    id
    title
    webappUri
    type
    listeningParty
    track {
      id
    }
    artist {
      id
      name
      webappUri
    }
    ...ReleasePlayButton
    ...LikeSoundMenuItem
  }
`);

export const ReleaseRow = ({
  releaseFrag,
  displayDropdownAsModal,
}: {
  releaseFrag: FragmentType<ReleaseRowListFragmentDoc>;
  playlistId: string;
  displayDropdownAsModal: boolean;
}) => {
  const release = getFragment(ReleaseRowListFragmentDoc, releaseFrag);

  const {
    title,
    artist: { name: artistName },
    track: { id: trackId },
    id: releaseId,
    webappUri,
    type,
    listeningParty,
  } = release;

  const currentListeningParty = useCurrentListeningParty({
    listeningParty,
    releaseId,
  });

  const { playing, activeTrackId } = useAudioController();

  const isActiveTrack = trackId === activeTrackId;
  const isPlaying = isActiveTrack && playing;

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const { isLiked, onClick } = useLikeRelease({ id: releaseId, source: 'Playlist Releases Modal' });
  const hasAudioAccess = release.type !== 'ALBUM';

  return (
    <RowContainer className="group">
      <Wrapper gap="sm">
        <ReleasePlayButton playButtonSize="m" releaseFrag={release} />
        <ReleaseInfo>
          <Wrapper>
            <PlayContentWrapper>
              {isPlaying && <PlayIndicatorBars size="xs" smallBars />}
              <AccessibleLink pathnameUri={webappUriPath(release.webappUri)}>
                <h2>{title}</h2>
              </AccessibleLink>
            </PlayContentWrapper>
          </Wrapper>
          <AccessibleLink pathnameUri={webappUriPath(release.artist.webappUri)}>
            <h3>{artistName}</h3>
          </AccessibleLink>
        </ReleaseInfo>
      </Wrapper>

      <Wrapper>
        <LikeButton liked={isLiked} onClick={onClick} disabled={type === 'ALBUM'} />

        <ThreeDotsDropdown
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
          dropdownProps={{
            align: 'end',
          }}
          isModal={displayDropdownAsModal}
        >
          <ViewSongMenuItem href={webappUriPath(webappUri)} />
          <AddToShelfMenuItem
            releaseId={releaseId}
            disabled={!hasAudioAccess}
            source="Playlist Releases Modal"
          />
          <AddToQueueMenuItem
            trackId={trackId}
            disabled={!trackId || !hasAudioAccess || !!currentListeningParty}
            emitAnalytics={() => {
              trackClickEvent({
                event: 'Add to Queue Clicked',
                properties: {
                  associationId: releaseId,
                  associationType: 'Release',
                  sourceComponent: 'Playlist Releases Modal',
                },
              });
            }}
          />
          <ShareMenuItem releaseId={release.id} source="Playlist Page" />
        </ThreeDotsDropdown>
      </Wrapper>
    </RowContainer>
  );
};

export const RowContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 64,
  marginRight: 6,
  borderRadius: 6,
  paddingX: 8,

  '.like-icon.liked': {
    visibility: 'visible',
  },

  '&:hover': {
    '.like-icon': {
      visibility: 'visible',
    },
  },

  '@tablet': {
    marginRight: 16,
  },

  '@tabletLandscape': {
    '.like-icon': {
      visibility: 'hidden',
    },
    '&:hover': {
      cursor: 'pointer',
      background: '$base50',
    },
  },
});

export const ReleaseInfo = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 4,

  h2: {
    title: 'XS',
    fontWeight: 500,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 160,
  },

  h3: {
    kicker: 'S',
    color: '$base700',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 160,
  },

  '@tablet': {
    h2: {
      maxWidth: 210,
    },
    h3: {
      maxWidth: 210,
    },
  },
});

export const Wrapper = styled('div', {
  display: 'flex',
  variants: {
    gap: {
      sm: {
        gap: 16,
      },
      md: {
        gap: 24,
      },
    },
  },
});
